import { Dispatch, SetStateAction, useCallback, useContext, useState } from "react";
import fraction from "src/api/fraction";
import { ApplicationFlowStateContext } from "src/apps/AppFlow/screens/root";
import { useModal } from "src/hooks";
import { useMutation } from "src/lib";

import { engine, enums } from "@fraction/shared";

const useCheckUnderwriting = ({
  onNext,
  getSpecificUnderwritingResult,
  failMessage,
  onChange,
}: {
  onChange?: Dispatch<SetStateAction<any>>;
  onNext: () => void;
  getSpecificUnderwritingResult: (result: engine.RunUnderwritingResults) =>
    | {
        message?: string;
        status: enums.EngineRuleStatus;
      }
    | undefined;
  failMessage: string | ((result: { message?: string; status: enums.EngineRuleStatus }) => string);
}) => {
  const { showModal, closeModal } = useModal();
  const [nextDisabled, setNextDisabled] = useState(false);

  const handleChange = useCallback(
    (args: any) => {
      setNextDisabled(false);
      onChange?.(args);
    },
    [onChange]
  );

  const {
    state: { id },
  } = useContext(ApplicationFlowStateContext);

  const handleCompleteUnderwriting = useCallback(
    (data: engine.RunUnderwritingResults) => {
      const result = getSpecificUnderwritingResult(data);
      if (!result) {
        return;
      }

      if (result.status === enums.EngineRuleStatus.FAILED) {
        setNextDisabled(true);
        showModal({
          message: typeof failMessage === "string" ? failMessage : failMessage(result),
        });
      } else {
        setNextDisabled(false);
        closeModal();
        onNext();
      }
    },
    [showModal, onNext, failMessage, closeModal, getSpecificUnderwritingResult]
  );

  const runUnderwriting = useMutation<engine.RunUnderwritingResults>({
    mutationFn: () => fraction.runUnderwriting(id),
    onSuccess: handleCompleteUnderwriting,
    meta: { errorMessage: "There was an error triggering a run of underwriting" },
  });

  return { ...runUnderwriting, nextDisabled, onChange: handleChange };
};

export default useCheckUnderwriting;
